import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Send, Info, ChevronDown, ChevronUp, Star, MessageSquare, Clock } from 'lucide-react';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchJobSkillsRequest } from '../../../redux/actions/jobSkillActions';
import { fetchNiveauSkillsRequest } from '../../../redux/actions/niveauSkillActions';
import { createSkillEvalRequest, fetchSkillEvalsByUserRequest } from '../../../redux/actions/skillEvaluationActions';
import './AutoEval.css';

export default function AutoEval() {
    const dispatch = useDispatch();

    const { user } = useSelector(state => state.auth || {});
    const { list: campagnesList, loading: loadingCamp } = useSelector(state => state.campagnes || { list: [] });
    const { niveauSkills } = useSelector(state => state.niveauSkill || { niveauSkills: [] });
    const { jobSkills } = useSelector(state => state.jobSkill || { jobSkills: [] });
    const { skillEvaluations, loading: evalLoading } = useSelector(state => state.skillEvaluation || { skillEvaluations: [] });

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [evaluations, setEvaluations] = useState({});
    const [commentaireGlobal, setCommentaireGlobal] = useState("");
    const [expandedAxes, setExpandedAxes] = useState([]);
    const [existingEval, setExistingEval] = useState(null);
    const [activeTab, setActiveTab] = useState("EVAL"); // "EVAL" ou "HISTORY"

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(fetchNiveauSkillsRequest());
        const roleId = user?.jobId || user?.posteId || user?.role?.id || user?.job?.id;
        if (roleId) dispatch(fetchJobSkillsRequest(roleId));
        if (user?.id || user?.uid) dispatch(fetchSkillEvalsByUserRequest(user.id || user.uid));
    }, [dispatch, user]);

    const activeCampagnes = useMemo(() =>
            (campagnesList || []).filter(c => c.status === true),
        [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    useEffect(() => {
        if (selectedCampagneId && skillEvaluations.length > 0) {
            const found = skillEvaluations.find(e => e.campagneId === parseInt(selectedCampagneId));
            if (found) {
                setExistingEval(found);
                setCommentaireGlobal(found.commentaireGlobal || "");
                const evals = {};
                found.niveaux?.forEach(n => { evals[n.skillId] = n.scoreObtenu; });
                setEvaluations(evals);
            } else {
                setExistingEval(null);
                setEvaluations({});
                setCommentaireGlobal("");
            }
        } else {
            setExistingEval(null);
        }
    }, [selectedCampagneId, skillEvaluations]);

    const isReadOnly = useMemo(() =>
            existingEval && existingEval.statut !== "EN_ATTENTE_COLLAB",
        [existingEval]);

    const formattedAxes = useMemo(() => {
        if (!jobSkills || jobSkills.length === 0) return [];
        
        // Filter to only include skills that are intended for Auto-evaluation
        const autoEvalSkills = jobSkills.filter(js => 
            js.modeEvaluationNom === "Auto-évaluation" || 
            js.modeEvaluationCode === "AUTO_EVAL"
        );

        const grouped = autoEvalSkills.reduce((acc, js) => {
            const key = js.skillType || "AUTRES";
            if (!acc[key]) {
                const nomGrp = key === "SOFT_SKILLS" ? "Soft Skills"
                    : key === "METIER" ? "Compétences Métier"
                        : "Autres Compétences";
                acc[key] = { id: key, nom: nomGrp, competences: [] };
            }
            if (!acc[key].competences.some(c => c.id === js.skillId)) {
                acc[key].competences.push({
                    id: js.skillId,
                    nom: js.skillNom,
                    description: js.skillDescription || "",
                });
            }
            return acc;
        }, {});
        return Object.values(grouped);
    }, [jobSkills]);


    useEffect(() => {
        if (formattedAxes.length > 0) setExpandedAxes(formattedAxes.map(a => a.id));
    }, [formattedAxes]);

    const sortedNiveaux = useMemo(() =>
            [...(niveauSkills || [])].sort((a, b) => (a.niveau || 0) - (b.niveau || 0)),
        [niveauSkills]);

    const toggleAxe = (axeId) =>
        setExpandedAxes(p => p.includes(axeId) ? p.filter(id => id !== axeId) : [...p, axeId]);

    const handleEval = (compId, levelId) => {
        if (isReadOnly) return;
        setEvaluations(p => ({ ...p, [compId]: levelId }));
    };

    const totalCompetences = formattedAxes.reduce((acc, a) => acc + a.competences.length, 0);
    const evaluees = Object.keys(evaluations).length;
    const progress = totalCompetences === 0 ? 0 : Math.round((evaluees / totalCompetences) * 100);

    const handleSubmit = () => {
        const niveauxArray = Object.entries(evaluations).map(([skillId, scoreObtenu]) => ({
            skillId: parseInt(skillId),
            scoreObtenu: parseInt(scoreObtenu),
            commentaire: ""
        }));
        dispatch(createSkillEvalRequest({
            campagneId: parseInt(selectedCampagneId),
            userId: user.id || user.uid || user.personnelId,
            statut: "EN_ATTENTE_COLLAB",
            dateEvaluation: new Date().toISOString(),
            commentaireGlobal,
            niveaux: niveauxArray,
            autoSubmit: true
        }));
    };

    return (
        <div className="ae-page">

            {/* ── Header ── */}
            <div className="ae-header">
                <div>
                    <h1>Mon espace évaluation</h1>
                    <p>Gérez vos auto-évaluations et consultez votre progression.</p>
                </div>
            </div>

            {/* ── Tabs ── */}
            <div className="ae-tabs">
                {[
                    { key: 'EVAL',    label: 'Évaluer' },
                    { key: 'HISTORY', label: 'Mon historique' },
                ].map(t => (
                    <button
                        key={t.key}
                        className={`ae-tab ${activeTab === t.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ── Campaign selector ── */}
            <div className="ae-campaign-bar">
                <label className="ae-campaign-label">Campagne active</label>
                {loadingCamp ? (
                    <span className="ae-loading-text">Chargement…</span>
                ) : (
                    <select
                        className="ae-select"
                        value={selectedCampagneId}
                        onChange={e => setSelectedCampagneId(e.target.value)}
                    >
                        <option value="">— Sélectionnez une campagne —</option>
                        {activeCampagnes.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.nom}{c.anneeCampagne ? ` (${c.anneeCampagne})` : ''}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* ── Main content ── */}
            <div className="ae-wrap">

                {/* ══ TAB: EVAL ══ */}
                {activeTab === 'EVAL' && (
                    <>
                        {selectedCampagneId ? (
                            <>
                                {/* Progress + submit */}
                                <div className="ae-progress-card">
                                    <div className="ae-progress-section">
                                        <div className="ae-progress-labels">
                                            <span className="ae-progress-lbl">Progression</span>
                                            <span className="ae-progress-pct">{progress}%</span>
                                        </div>
                                        <div className="ae-progress-track">
                                            <div className="ae-progress-fill" style={{ width: `${progress}%` }} />
                                        </div>
                                    </div>
                                    {!isReadOnly && (
                                        <button
                                            className="ae-btn ae-btn--primary"
                                            disabled={progress < 100 || evalLoading}
                                            onClick={handleSubmit}
                                        >
                                            <Send size={13} />
                                            {evalLoading ? "Envoi…" : "Soumettre l'évaluation"}
                                        </button>
                                    )}
                                </div>

                                {/* Read-only banner */}
                                {isReadOnly && (
                                    <div className="ae-info-banner">
                                        <Info size={14} />
                                        <span>Dossier soumis. Statut actuel : <strong>{existingEval.statut}</strong></span>
                                    </div>
                                )}

                                {/* Axes */}
                                {formattedAxes.map(axe => (
                                    <div key={axe.id} className="ae-axe-card">
                                        <div className="ae-axe-header" onClick={() => toggleAxe(axe.id)}>
                                            <div className="ae-axe-title-wrap">
                                                <span className="ae-axe-title">{axe.nom}</span>
                                                <span className="ae-badge">{axe.competences.length}</span>
                                            </div>
                                            {expandedAxes.includes(axe.id)
                                                ? <ChevronUp size={15} className="ae-chev" />
                                                : <ChevronDown size={15} className="ae-chev" />}
                                        </div>

                                        {expandedAxes.includes(axe.id) && (
                                            <div className="ae-axe-body">
                                                {axe.competences.map(comp => {
                                                    const existingLevel = existingEval?.niveaux?.find(n => n.skillId === comp.id);
                                                    return (
                                                        <div key={comp.id} className={`ae-comp-row${isReadOnly ? ' ae-comp-row--readonly' : ''}`}>
                                                            <div className="ae-comp-info">
                                                                <div className="ae-comp-name">{comp.nom}</div>
                                                                {comp.description && (
                                                                    <div className="ae-comp-desc">{comp.description}</div>
                                                                )}
                                                            </div>

                                                            {isReadOnly ? (
                                                                <div className="ae-scores-grid">
                                                                    <div className="ae-score-col">
                                                                        <div className="ae-score-lbl">Moi</div>
                                                                        <div className="ae-score-val">{existingLevel?.scoreCollab ?? "—"}</div>
                                                                    </div>
                                                                    <div className="ae-score-col">
                                                                        <div className="ae-score-lbl">Manager</div>
                                                                        <div className="ae-score-val ae-score-val--blue">{existingLevel?.scoreManager ?? "—"}</div>
                                                                    </div>
                                                                    <div className="ae-score-col">
                                                                        <div className="ae-score-lbl">RH / Final</div>
                                                                        <div className="ae-score-val ae-score-val--coral">{existingLevel?.scoreObtenu ?? "—"}</div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="ae-levels">
                                                                    {sortedNiveaux.map(niv => (
                                                                        <button
                                                                            key={niv.id}
                                                                            className={`ae-level-btn${evaluations[comp.id] === niv.id ? ' active' : ''}`}
                                                                            onClick={() => handleEval(comp.id, niv.id)}
                                                                            title={niv.nom}
                                                                        >
                                                                            {niv.niveau}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* Commentaire global */}
                                <div className="ae-comment-card">
                                    <div className="ae-section-title">
                                        <MessageSquare size={14} />
                                        Commentaire global
                                    </div>
                                    <textarea
                                        className="ae-textarea"
                                        placeholder="Vos observations générales…"
                                        value={commentaireGlobal}
                                        onChange={e => setCommentaireGlobal(e.target.value)}
                                        rows={3}
                                        disabled={isReadOnly}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="ae-empty">
                                <Star size={40} className="ae-empty-icon" />
                                <h3>Sélectionnez une campagne</h3>
                                <p>Choisissez une campagne dans le sélecteur ci-dessus pour accéder à votre évaluation.</p>
                            </div>
                        )}
                    </>
                )}

                {/* ══ TAB: HISTORY ══ */}
                {activeTab === 'HISTORY' && (
                    <>
                        {skillEvaluations.filter(e =>
                            (typeof e.statut === 'string' ? e.statut : e.statut?.name) === 'VALIDEE'
                        ).length === 0 ? (
                            <div className="ae-empty">
                                <Clock size={40} className="ae-empty-icon" />
                                <h3>Aucun historique validé</h3>
                                <p>Vos évaluations validées apparaîtront ici.</p>
                            </div>
                        ) : (
                            skillEvaluations
                                .filter(e => (typeof e.statut === 'string' ? e.statut : e.statut?.name) === 'VALIDEE')
                                .map(ev => (
                                    <div key={ev.id} className="ae-history-card">
                                        <div className="ae-history-head">
                                            <span className="ae-history-title">{ev.campagneNom}</span>
                                            <span className="ae-history-date">
                                                Clôturée le {new Date(ev.dateEvaluation).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <div className="ae-table-wrap">
                                            <table className="ae-table">
                                                <thead>
                                                <tr>
                                                    <th>Compétence</th>
                                                    <th>Auto</th>
                                                    <th>Manager</th>
                                                    <th>Final (RH)</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {ev.niveaux?.map(n => (
                                                    <tr key={n.id}>
                                                        <td className="ae-table-name">{n.skillNom}</td>
                                                        <td><span className="ae-score-pill">{n.scoreCollab}</span></td>
                                                        <td><span className="ae-score-pill ae-score-pill--blue">{n.scoreManager}</span></td>
                                                        <td><span className="ae-score-pill ae-score-pill--coral">{n.scoreObtenu}</span></td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))
                        )}
                    </>
                )}

            </div>
        </div>
    );
}